import { useEffect, useState } from 'react'
import { getEnvironment } from 'src/config/env'
import { useSelector } from 'react-redux'
import { selectMarketState, selectPostalCode } from 'src/modules/reducers/market'
import { teamData } from 'src/constants/teamData'
import { getUniqueLeagues } from 'src/modules/utils/league'
import { fanduelPackages } from 'src/constants/planData/fanduelPackageData'

const env = getEnvironment()

const defaultSportType = 'mlb'

const planCardImages = {
  mlb: 'https://assets-imgx.fubo.tv/marketing/lp/images/card/us/fanduel-poster-plan-card.png',
  nba: 'https://assets-imgx.fubo.tv/marketing/lp/images/card/rsn_basketball_hockey_plan_card.jpg',
  nfl: 'https://assets-imgx.fubo.tv/marketing/lp/images/card/rsn_basketball_hockey_plan_card.jpg',
  nhl: 'https://assets-imgx.fubo.tv/marketing/lp/images/card/rsn_basketball_hockey_plan_card.jpg',
  default:
    'https://assets-imgx.fubo.tv/marketing/lp/images/card/rsn_basketball_hockey_plan_card.jpg',
}

const processTeamsBySportType = (teams, sportType, uniqueLeagues, IsSportTypeOnly) => {
  let sportTypeVal = sportType

  //Check if the incoming sport type is in the unique leagues
  const hasSportType = uniqueLeagues.includes(sportTypeVal)
  if (!hasSportType && IsSportTypeOnly) return null

  //If the incoming sport type is not in the unique leagues, use the default sport type
  if (!sportTypeVal) {
    sportTypeVal = uniqueLeagues.includes(defaultSportType) ? defaultSportType : null
  }

  //Sort the teams by the sport type
  const sortedTeams = [...teams].sort((a, b) => {
    const aIsSport = teamData[a]?.league === sportTypeVal
    const bIsSport = teamData[b]?.league === sportTypeVal
    return aIsSport && !bIsSport ? -1 : !aIsSport && bIsSport ? 1 : 0
  })

  return { teams: sortedTeams, sportTypeValue: sportTypeVal }
}

export const useCheckFanduelRSN = ({ sportType = null, IsSportTypeOnly = false } = {}) => {
  const postalCode = useSelector(selectPostalCode)
  const { regionCode } = useSelector(selectMarketState)
  const [rsnData, setRSNData] = useState(null)

  useEffect(() => {
    if (!postalCode || !regionCode) return

    let isCleanedUp = false

    const fetchData = async () => {
      try {
        const response = await fetch(`${env.default.host}/data/fd_data/fd_${regionCode}.json`)
        if (!response.ok || isCleanedUp) return null

        const data = await response.json()
        const postalCodeData = data[postalCode]
        if (!postalCodeData?.teams || isCleanedUp) return null

        const uniqueLeagues = getUniqueLeagues(postalCodeData.teams)
        const processedTeams = processTeamsBySportType(
          postalCodeData.teams,
          sportType,
          uniqueLeagues,
          IsSportTypeOnly
        )

        if (!processedTeams || isCleanedUp) return null

        const { teams, sportTypeValue } = processedTeams
        const pkg = postalCodeData.package
        if (!fanduelPackages[pkg]?.offerCode || isCleanedUp) return null

        return {
          ...postalCodeData,
          ...fanduelPackages[pkg],
          promoImg: planCardImages[sportTypeValue] || planCardImages.default,
          teams,
          regionCode,
        }
      } catch (error) {
        if (!isCleanedUp) {
          console.error('Error fetching Fanduel RSN data:', error)
        }
        return null
      }
    }

    fetchData().then((result) => {
      if (!isCleanedUp) setRSNData(result)
    })

    return () => {
      isCleanedUp = true
    }
  }, [postalCode, regionCode, sportType, IsSportTypeOnly])

  return {
    showFDPlanCards: !!rsnData,
    isAvailable: !!rsnData,
    rsnData,
  }
}
